import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppForm } from 'src/component/common/analog/molecule/basic/AppForm'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useNotificationStore } from 'src/feature/notification/store/notification.store'
import { useProjectBuilderStore } from '../../store/project-builder.store'
import { UserWithMatchedSkills } from '../../model/project-builder'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'
import { CircularProgress, Tooltip, Typography } from '@mui/material'
import { AppAvatar } from 'src/component/common/analog/molecule/AppAvatar'
import { API_BASE_URL } from 'src/util/constant'
import { ProjectNotificationPrompt } from './ProjectNotificationPrompt'

export type CreateProjectModalProps = {
  open: boolean
  onClose: () => void
  selectedUserIds: Set<string>
  allUsers: UserWithMatchedSkills[]
}

export const CreateProjectModal = observer(
  ({ open, onClose, selectedUserIds, allUsers }: CreateProjectModalProps) => {
    const { translation } = useLocalizationStore()
    const notificationStore = useNotificationStore()
    const projectBuilderStore = useProjectBuilderStore()
    const projectBuilderTranslation = translation.pages.projectBuilder

    const [projectName, setProjectName] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    // Prevent duplicate submissions while request is running
    const submittingRef = useRef(false)
    const [showNotificationPrompt, setShowNotificationPrompt] = useState(false)

    // Auto-fill name, start date and end date from store (or default values)
    useEffect(() => {
      if (open) {
        setProjectName(projectBuilderStore.projectName)
        // Add default values to date if not in store
        if (projectBuilderStore.startDate) {
          setStartDate(projectBuilderStore.startDate)
        } else {
          // Default today's date
          const today = new Date()
          setStartDate(today.toISOString().split('T')[0])
        }

        if (projectBuilderStore.endDate) {
          setEndDate(projectBuilderStore.endDate)
        } else {
          // Default today's date + 6 months
          const today = new Date()
          const sixMonthsLater = new Date(today)
          sixMonthsLater.setMonth(today.getMonth() + 6)
          setEndDate(sixMonthsLater.toISOString().split('T')[0])
        }
      }
    }, [
      open,
      projectBuilderStore.projectName,
      projectBuilderStore.startDate,
      projectBuilderStore.endDate,
    ])

    const handleClose = useCallback(() => {
      setProjectName('')
      setStartDate('')
      setEndDate('')
      setIsSubmitting(false)
      submittingRef.current = false
      setShowNotificationPrompt(false)
      onClose()
    }, [onClose])

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      // Prevent multiple submissions
      if (submittingRef.current || isSubmitting) {
        return
      }

      // Validate required fields before creating project
      if (!projectName.trim() || !projectBuilderStore.projectDescription.trim() || !startDate || !endDate) {
        notificationStore.enqueueError(projectBuilderTranslation.requiredFields())
        
        return
      }

      // Validate that end date is not before start date
      if (new Date(startDate) > new Date(endDate)) {
        notificationStore.enqueueError(projectBuilderTranslation.endDateValidation())
        
        return
      }
      setShowNotificationPrompt(true)
    },
    [notificationStore, projectBuilderStore, projectName, startDate, endDate, projectBuilderTranslation, isSubmitting])

    // Project is created once the manager selects if they want to send notifications to invited users
    const handleCreateProject = useCallback(
      async (notifyUsers: boolean) => {
        if (submittingRef.current || isSubmitting) {
          return
        }

        submittingRef.current = true
        setIsSubmitting(true)

        try {
          // Create project with selected users
          await projectBuilderStore.createProject(
            projectName,
            startDate,
            endDate,
            Array.from(selectedUserIds),
            notifyUsers,
          )

          notificationStore.enqueueSuccess(projectBuilderTranslation.createProjectSuccess())
          setShowNotificationPrompt(false)
          handleClose()
        } catch (error) {
          notificationStore.enqueueError(
            error instanceof Error
              ? error.message
              : projectBuilderTranslation.createProjectFailed(),
          )
        } finally {
          // Reset submitting state after request finishes
          submittingRef.current = false
          setIsSubmitting(false)
        }
      },
      [notificationStore, projectBuilderStore, projectName, startDate, endDate, selectedUserIds, handleClose, projectBuilderTranslation, isSubmitting],
    )

    // Merge duplicate users into one user card while combining all matched skills for that user
    const selectedUsers = useMemo(() => {
      const userMap = new Map<string, UserWithMatchedSkills>()

      allUsers.forEach((user) => {
        // Ignore users that are not selected
        if (!selectedUserIds.has(user.userId)) {
          return
        }

        const existingUser = userMap.get(user.userId)

        // If user already exists, merge matched skills
        if (existingUser) {
          existingUser.matchedSkills = [
            ...existingUser.matchedSkills,
            ...user.matchedSkills,
          ]

          return
        }

        // First occurrence of user
        userMap.set(user.userId, {
          ...user,
          matchedSkills: [...user.matchedSkills],
        })
      })

      return Array.from(userMap.values())
    }, [allUsers, selectedUserIds])

    const renderSelectedUser = (user: UserWithMatchedSkills) => {
      // Format roles for cleaner UI
      const formatRoles = (roles: string[]) => {
        if (!roles.length) {
          return ''
        }

        if (roles.length <= 2) {
          return roles.join(', ')
        }

        return `${roles.slice(0, 2).join(', ')} +${roles.length - 2}`
      }

      return (
        <AppBox key={user.userId} sx={styles.userCard}>
          <AppAvatar
            src={`${API_BASE_URL}${user.avatarUrl}`}
            outerProps={{ squareSize: 42 }}
          />

          <AppBox sx={styles.userInfo}>
            <Typography sx={styles.userName}>
              {user.userName}
            </Typography>

            {Boolean(user.roles?.length) && (
              <Typography sx={styles.userRoles}>
                {formatRoles(user.roles)}
              </Typography>
            )}

            <Tooltip
              title={user.matchedSkills
                .map(skill => skill.organizationSkillName)
                .join(', ')}
              arrow
              placement="top"
            >
              <Typography sx={styles.userSkill}>
                {user.matchedSkills
                  .map(skill => skill.organizationSkillName)
                  .join(', ')}
              </Typography>
            </Tooltip>
          </AppBox>
        </AppBox>
      )
    }

    return (
      <>
        <ModalWindow
          modalProps={{
            open,
            onClose: handleClose,
            PaperProps: {
              sx: {
                maxWidth: { xs: '90%', md: '60%', lg: '35%' },
                borderRadius: '20px',
                backgroundColor: Palette.WHITE,
              },
            },
          }}
        >
          <AppForm sx={styles.form} onSubmit={handleSubmit}>
            <AppTextField
              m={0}
              label={projectBuilderTranslation.projectName()}
              value={projectName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
              fullWidth
              required
            />
            <AppBox sx={styles.dateSection}>
              <AppBox sx={styles.dateFieldWrapper}>
                <AppTextField
                  m={0}
                  label={projectBuilderTranslation.startDate()}
                  type="date"
                  value={startDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </AppBox>

              <AppBox sx={styles.dateFieldWrapper}>
                <AppTextField
                  m={0}
                  label={projectBuilderTranslation.endDate()}
                  type="date"
                  value={endDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </AppBox>
            </AppBox>

            <AppBox sx={styles.selectedUsersSection}>
              <Caption>{projectBuilderTranslation.selectedUsers()} ({selectedUsers.length})</Caption>
              <AppBox sx={styles.usersList}>
                {selectedUsers.map(renderSelectedUser)}
              </AppBox>
            </AppBox>

            <AppBox sx={styles.footer}>
              <AppButton
                sx={{ width: '20em' }}
                variant="contained"
                textValue={projectBuilderTranslation.createProjectButton()}
                fontSize={14}
                disabled={isSubmitting || !projectName.trim()}
                endIcon={isSubmitting ? <CircularProgress size={16} color='inherit' sx={{ margin: '0px 3px' }} /> : null}
                type="submit"
              />
            </AppBox>
          </AppForm>
        </ModalWindow>
        <ProjectNotificationPrompt
          open={showNotificationPrompt}
          onClose={() => setShowNotificationPrompt(false)}
          onConfirm={handleCreateProject}
          isSubmitting={isSubmitting}
          description={projectBuilderTranslation.notifyUsersDescription()}
          yesText={projectBuilderTranslation.notifyUsersYes()}
          noText={projectBuilderTranslation.notifyUsersNo()}
        />
      </>
    )
  },
)

const styles = {
  form: {
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  dateSection: {
    display: 'flex',
    gap: 20,
    width: '100%',
    mt: 20,
  },
  dateFieldWrapper: {
    flex: 1,
    flexDirection: 'column',
    gap: 6,
  },
  selectedUsersSection: {
    flexDirection: 'column',
    gap: 10,
    mt: 20,
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: '250px',
    overflowY: 'auto',
    pr: 5,
  },
  userCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px',
    borderRadius: '12px',
    border: `1px solid ${ColorTokens.neutral300}`,
    backgroundColor: ColorTokens.neutral100,
    minHeight: '90px',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontSize: 12,
    fontWeight: 600,
    color: 'text.primary',
  },
  userRoles: {
    fontSize: 12,
    color: Palette.CORAL_REEF_GREEN,
  },
  userSkill: {
    fontSize: 12,
    color: 'text.secondary',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  footer: {
    marginTop: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
}